import {useEffect} from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  nextPage,
  previousPage,
  questionAnswered,
  selectCostQuestions,
  selectPage,
  resetPage,
  selectCostComplete
} from "../features/cost/costSlice.js";
import costIcon from "../assets/Navigation/nav_icon_cost_green.png";
import SideNav from "../components/SideNav";
import PageTitle from "../components/PageTitle";
import { Link } from "react-router-dom";
import configA from '../assets/well_config_question/question_well_config_A.jpg'
import configB from '../assets/well_config_question/question_well_config_B.jpg'
import configC from '../assets/well_config_question/question_well_config_C.jpg'
import capex from '../assets/Popups/popup_capex_graphic.jpg'
import capexStat from '../assets/Popups/popup_industry_stat_50.png'
import SlideOut from "../components/SlideOut.js";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { ArrowBackIos } from "@mui/icons-material";
import { Button, Container } from "@mui/material";

const Cost = () => {
  const page = useSelector(selectPage)
  const questions = useSelector(selectCostQuestions)
  const dispatch = useDispatch();
  const section = {
    title: "Cost Savings",
    icon: costIcon,
    pages: [1, 2, 3],
  };

  const isComplete = useSelector(selectCostComplete)
  useEffect(
    ()=>!isComplete ? dispatch(resetPage()):null,[]
  )

  const capexSavings = {
    title: 'Reduce Operational Costs',
    copy: 'Using Roxar 2600 Multiphase Flow Meter (MPFM) can help your operation reduce costs by:',
    bullets: [<li>Reduce maintenance interventions</li>,<li>Reduce spare parts/reduce inventory</li>,<li>Efficient remediation plans through immediate data insight—planned maintenance as opposed to unplanned/emergency</li>],
    stat:{
      copy:`On initial well pad construction, switching from a separator per well to a multiphase meter per well.*`,
      graphic: capexStat
    },
    view:{
      vert:true,
      copy:['View graphic:',<br/>,'CAPEX differential'],
      header:['CAPEX $ Differential:'],
      paragraph:['(% averaged, from North American producers for separators, instruments, flow lines, tanks)'],
      graphic: capex
    }
  }

  return (
    <Container className="content_container" maxWidth='lg'>
    <div className="roxar_content">
      <div className="main_content">
        <PageTitle section={section} currentPage={page} />
        <div className="page_contents">
          {page === 1 && (
            <div className="challenges"> 
              <div>
                <h1>Production Challenges</h1>
                <ul>
                  <li>
                 CAPEX spend on separators, flowlines, etc  
                  </li>
                  <li>
                  OPEX spend on unplanned and increased maintenance, spare parts 
                  </li>
                  <li>
                  Cost of troubleshooting/delay from upset to remediation plan to fix
                  </li>
                </ul>
              </div>
              <div className="solution_copy">
                <p>
                Poor separation and frequent maintenance can put pressure on your bottom line. With fewer maintenance interventions Roxar 2600 Multiphase Flow Meter (MPFM) can help keep costs low and significantly reduce CAPEX investment.
                </p>
                <Button className="forward_button production_button" onClick={() => dispatch(nextPage())}>Learn How</Button>
              </div>
            </div>
          )}
          {page === 2 && (
            <div className="questions">
              <div>
                  <h3 className="question">Q: Choose the configuration that best matches your wellpad.</h3>
                  <div className="answers">
                      <div className="well_pad_answer">
                      <Button className={`answer ${questions[0].answer=='A'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'A', score:1, max: 3}))}> A</Button><p>One test sep per well with one production separator for overall well pad</p><img className='well_pad_image' src={configA} />

                      </div>
                      <div className="well_pad_answer">
                      <Button className={`answer ${questions[0].answer=='B'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'B', score:2, max: 3}))}> B</Button><p>One test sep on a manifold handling multiple wells, with one production separator for overall well pad</p><img className='well_pad_image' src={configB} />

                      </div>
                      <div className="well_pad_answer">
                      <Button className={`answer ${questions[0].answer=='C'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'C', score:3, max: 3}))}> C</Button><p>One test sep on manifold handling multiple wells, with production separator downstream, off pad (co-mingled pads, common royalty interests)</p><img className='well_pad_image' src={configC} />

                      </div>
                      <div className="well_pad_answer">
                      <Button className={`answer ${questions[0].answer=='D'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'D', score:0, max: 0}))}>D</Button> <p>Other</p>

                      </div>
                  </div>
              </div>
              <div className="page_nav">
                  <Button className="prev_button" onClick={() => dispatch(previousPage())}><ArrowBackIos /><span>Previous</span></Button>
                  <Button className="next_button" onClick={() => dispatch(nextPage())}><span>Next</span><ArrowForwardIos /></Button>
              </div>
            </div>
          )}
          {page === 3 && (
              <div className="questions">
              <div>
                  <h3 className="question">Q: What is your average CAPEX spend on a fully instrumented test separator?</h3>
                  <div className="answers">
                  <Button className={`answer ${questions[1].answer=='<$100k'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'<$100k', score:5, max: 5}))}> &lt;$100k</Button>
                  <Button className={`answer ${questions[1].answer=='$100k-$250k'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'$100k-$250k', score:4, max: 5}))}> $100k-$250k</Button>
                  <Button className={`answer ${questions[1].answer=='$250k-$500k'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'$250k-$500k', score:3, max: 5}))}> $250k-$500k</Button>
                  <Button className={`answer ${questions[1].answer=='$500k-$1M'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'$500k-$1M', score:2, max: 5}))}> $500k-$1M</Button>   
                  <Button className={`answer ${questions[1].answer=='>$1M'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'>$1M', score:1, max: 5}))}> &gt;$1M</Button>   
                  </div>
              </div>
              <div>
                  <h3 className="question">Q: In regards to separators and associated equipment, what is the estimated dollar value of spare parts/inventory you keep on hand?</h3>
                  <div className="answers">
                  <Button className={`answer ${questions[2].answer=='None. (Order as needed)'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'None. (Order as needed)', score:1, max: 3}))}> None. (Order as needed)</Button>
                  <Button className={`answer ${questions[2].answer=='<$10k'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'<$10k', score:2, max: 3}))}> &lt;$10k</Button>  
                  <Button className={`answer ${questions[2].answer=='$10k-$50k'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'$10k-$50k', score:3, max: 3}))}> $10k-$50k</Button>  
                  <Button className={`answer ${questions[2].answer=='$50k-$100k'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'$50k-$100k', score:2, max: 3}))}>$50k-$100k</Button>  
                  <Button className={`answer ${questions[2].answer=='>$100k'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'>$100k', score:1, max: 3}))}> &gt;$100k</Button>  
                  </div>
              </div>
              <div className="page_nav">
              <Button className="prev_button" onClick={() => dispatch(previousPage())}><ArrowBackIos /><span>Previous</span></Button>
                  <Button className="next_button"  component={Link} to={'/results'}><span>Next</span><ArrowForwardIos /></Button>
              </div>
              <div>
                  <Button component={Link} to='/results' className="next_button view_results">View Section Results</Button>
              </div>
              <div className='bottom_filler'>

              </div>
            </div>
          )}
        </div>
      </div>
      {page === 3 && (
      <SlideOut title={capexSavings.title} copy={capexSavings.copy}  bullets={capexSavings.bullets} view={capexSavings.view} stat={capexSavings.stat}/>
    )}
      <SideNav />
    </div>
    </Container>
  );
};

export default Cost;